import { useCallback, useEffect, useRef, useState } from "react";
import getSocket from "../services/sockets/socketClient";

const LOG_PREFIX = "[useWidgetRuntime]";

/**
 * Suscribe un widget al singleton en tiempo real del backend
 * (widgetRuntimeManager). Mientras el componente esté montado, el widget
 * recibe un `widget:data` cada vez que el runtime compartido hace un tick
 * (cada 5s por defecto, parametrizable en el backend) — sin importar
 * cuántos otros dashboards estén mirando el mismo widget+filtro, el backend
 * solo corre la consulta pesada una vez.
 *
 * @param {string} widgetId
 * @param {string} dashboardId
 * @param {object} [options] - para widgets con paginación/orden/filtro
 *   propios del viewer (ej. tablas): cada combinación de opciones distinta
 *   se suscribe a su propio runtime en el backend. Debe ser un objeto
 *   estable en forma (se serializa internamente para comparar cambios).
 * @param {any} [resubscribeToken] - valor cualquiera (ej. el dateRange del
 *   dashboard serializado) que, al cambiar, fuerza una re-suscripción. Sirve
 *   para widgets en modo "byDashboard": cuando el filtro de fecha del
 *   dashboard cambia, el backend resuelve una key nueva, pero nadie vuelve a
 *   pedirla hasta que el cliente se vuelve a suscribir. No se envía al
 *   servidor, solo dispara el efecto.
 * @returns {{ data: any, error: string|null, loading: boolean, refresh: () => void }}
 *   `refresh` fuerza una re-suscripción (útil después de guardar cambios de
 *   configuración del propio widget: el backend ya invalida el runtime
 *   viejo, pero nadie vuelve a pedir uno nuevo hasta el próximo subscribe).
 */
export default function useWidgetRuntime(widgetId, dashboardId, options, resubscribeToken) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const optionsKey = JSON.stringify(options || {});
  const subscribeRef = useRef(() => {});

  useEffect(() => {
    if (!widgetId) return;

    const socket = getSocket();
    const parsedOptions = JSON.parse(optionsKey);

    setLoading(true);

    const onData = (msg) => {
      if (msg.widgetId !== widgetId) return;
      console.log(LOG_PREFIX, "data recibida", { widgetId, ts: msg.ts });
      setData(msg.data);
      setError(null);
      setLoading(false);
    };

    const onError = (msg) => {
      if (msg.widgetId !== widgetId) return;
      console.warn(LOG_PREFIX, "error recibido", { widgetId, message: msg.message });
      setError(msg.message);
      setLoading(false);
    };

    socket.on("widget:data", onData);
    socket.on("widget:error", onError);

    const doSubscribe = () => {
      console.log(LOG_PREFIX, "subscribe ->", { widgetId, dashboardId, options: parsedOptions });
      socket.emit("widget:subscribe", { widgetId, dashboardId, options: parsedOptions });
    };
    subscribeRef.current = doSubscribe;

    if (socket.connected) doSubscribe();
    socket.on("connect", doSubscribe);

    return () => {
      console.log(LOG_PREFIX, "unsubscribe ->", { widgetId, dashboardId, options: parsedOptions });
      socket.emit("widget:unsubscribe", { widgetId, dashboardId, options: parsedOptions });
      socket.off("widget:data", onData);
      socket.off("widget:error", onError);
      socket.off("connect", doSubscribe);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetId, dashboardId, optionsKey, resubscribeToken]);

  const refresh = useCallback(() => {
    subscribeRef.current();
  }, []);

  return { data, error, loading, refresh };
}
